// One generation job (contracts/studio.md §4): its counts, tokens and cost, then every candidate rendered
// with @lumen/puzzle-render, its verification badge (solver ✓ unique / needs review / failed) and issues,
// the answer and explanation behind a reveal (R), and Approve / Reject / Edit for the bank drafts.

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { PuzzleView, type PuzzleSpec } from "@lumen/puzzle-render";
import { AnswerSchema, PuzzleSpecSchema, type AnswerValue } from "../../api/schemas";
import { useStudioJob } from "../../api/studio";
import type { StudioJob, StudioJobItem } from "../../api/studioSchemas";
import { useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, EmptyState, ErrorState, Loading, PageHeader, Segmented, Stat, StatusChip } from "../../components/ui";
import { formatDateTime, formatShortDate } from "../../lib/dates";
import { fmtCompact, fmtInt, typeSlug } from "../../lib/format";
import { useHotkey } from "../../lib/hotkeys";
import { fmtUsd, isActiveJob, itemBadge, splitJobError, STUDIO_ERRORS } from "../../lib/studio";
import pages from "../pages.module.css";
import { AnswerText, BackLink, Callout, IssuesList, JobStatusChip, KindChip, ReviewActions, RoleNote, VerificationChip } from "./components";
import { SpecEditorDialog } from "./dialogs";
import { CancelJob, jobDuration, JobProgress, LiveBadge } from "./jobs";
import { LevelPackJob } from "../levelpacks/LevelPackJob";
import s from "./studio.module.css";

type Filter = "all" | "review" | "created" | "failed";
const PAGE = 24;

const matches = (i: StudioJobItem, f: Filter) =>
  f === "all" || (f === "review" ? i.status === "needs_review" : f === "created" ? i.status === "created" : i.status === "rejected_by_verifier");

export function StudioJobPage() {
  const { id = "" } = useParams();
  const q = useStudioJob(id);
  const navigate = useNavigate();
  const [reveal, setReveal] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [shown, setShown] = useState(PAGE);
  const [editing, setEditing] = useState<string | null>(null);
  const canEdit = useCan("studio.edit");
  useHotkey("r", () => setReveal((v) => !v), Boolean(q.data));

  const back = <BackLink to="/studio/jobs">Jobs</BackLink>;
  if (q.isPending) {
    return (
      <>
        <PageHeader title={<span className="mono">{id.slice(0, 8)}</span>} meta={back} />
        <Loading rows={10} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        <PageHeader title={<span className="mono">{id.slice(0, 8)}</span>} meta={back} />
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title={(q.error as { status?: number }).status === 404 ? "No such job" : "Couldn't load this job"} />
      </>
    );
  }

  const { job, items } = q.data;
  if (job.kind === "levelpack") return <LevelPackJob job={job} packs={q.data.packs ?? []} back={back} />;
  const counts: Record<Filter, number> = {
    all: items.length,
    review: items.filter((i) => matches(i, "review")).length,
    created: items.filter((i) => matches(i, "created")).length,
    failed: items.filter((i) => matches(i, "failed")).length,
  };
  const visible = items.filter((i) => matches(i, filter));
  const active = isActiveJob(job.status);

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
            {job.kind === "import" ? "Curated import" : typeSlug(job.type)}
            <KindChip kind={job.kind} />
          </span>
        }
        meta={
          <>
            {back}
            <JobStatusChip status={job.status} cancelRequested={job.cancelRequested} />
            <span className="mono">{job.type}</span>
            <span className="mono">{job.id}</span>
          </>
        }
        actions={
          <>
            <RoleNote />
            {active ? <LiveBadge live /> : null}
            <CancelJob job={job} size="md" />
            {job.kind !== "import" && canEdit ? (
              <Button variant="ghost" onClick={() => navigate(`/studio/new?type=${encodeURIComponent(job.type)}&kind=${job.kind}`)}>
                Same again
              </Button>
            ) : null}
          </>
        }
      />

      <JobSummary job={job} />

      <div className={s.toolbar}>
        <Segmented<Filter>
          label="Show"
          value={filter}
          onChange={(f) => {
            setFilter(f);
            setShown(PAGE);
          }}
          options={[
            { value: "all", label: `All ${counts.all}` },
            { value: "review", label: `Needs review ${counts.review}` },
            { value: "created", label: `Created ${counts.created}` },
            { value: "failed", label: `Failed ${counts.failed}` },
          ]}
        />
        <div className={s.toolbarEnd}>
          <Button size="sm" variant={reveal ? "primary" : "secondary"} aria-pressed={reveal} onClick={() => setReveal((v) => !v)} shortcut="R">
            {reveal ? "Hide answers" : "Reveal answers"}
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState title={active ? "Nothing yet" : "No candidates"}>{active ? "Candidates appear here batch by batch." : "This job produced no candidates."}</EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState title="None in this view" />
      ) : (
        <div className={s.itemGrid} data-testid="job-items">
          {visible.slice(0, shown).map((item) => (
            <ItemCard key={item.id} item={item} reveal={reveal} onEdit={setEditing} />
          ))}
        </div>
      )}
      {visible.length > shown ? (
        <div className={s.more}>
          <Button variant="ghost" onClick={() => setShown((n) => n + PAGE)}>
            Show {Math.min(PAGE, visible.length - shown)} more · {visible.length - shown} left
          </Button>
        </div>
      ) : null}

      <SpecEditorDialog id={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function JobSummary({ job }: { job: StudioJob }) {
  const err = splitJobError(job.error);
  const known = err?.code ? STUDIO_ERRORS[err.code] : undefined;
  const d = job.params.difficulty;
  return (
    <Card>
      <div className={s.jobStrip}>
        <Stat label="Requested" value={fmtInt(job.counts.requested)} sub="candidates asked for" />
        <Stat label="Created" value={fmtInt(job.counts.created)} sub="solver-verified drafts" />
        <Stat label="Failed verification" value={fmtInt(job.counts.failedVerification)} sub="incl. duplicates" />
        <Stat label="Needs review" value={fmtInt(job.counts.needsReview)} sub="no solver: a person" />
        <Stat label="Tokens in / out" value={job.kind === "ai" ? `${fmtCompact(job.tokens.input)} / ${fmtCompact(job.tokens.output)}` : "—"} sub={job.kind === "ai" ? "incl. cache" : "procedural: none"} />
        <Stat label="Cost" value={job.kind === "ai" ? fmtUsd(job.costUsd) : "free"} sub={job.model ?? "no model"} />
      </div>
      <JobProgress job={job} />
      <div className={s.jobMeta}>
        {job.promptVersion ? (
          <span>
            prompt <b className="mono">{job.promptVersion}</b>
          </span>
        ) : null}
        {job.params.theme ? (
          <span>
            theme <b>“{job.params.theme}”</b>
          </span>
        ) : null}
        {d ? (
          <span>
            difficulty <b className="mono">{d.min.toFixed(2)}–{d.max.toFixed(2)}</b>
          </span>
        ) : null}
        <span>
          requested by <b>{job.requestedBy ?? "CLI (pnpm seed:curated)"}</b>
        </span>
        <span>
          created <b>{formatDateTime(job.createdAt)}</b>
        </span>
        {job.finishedAt ? (
          <span>
            finished <b>{formatDateTime(job.finishedAt)}</b>
          </span>
        ) : null}
        <span>
          took <b className="mono">{jobDuration(job)}</b>
        </span>
      </div>
      {err ? (
        <div style={{ marginTop: 14 }}>
          <Callout tone="danger" title={known?.title ?? "The job failed"} code={err.code ?? undefined} testId="job-error">
            <p>{err.message}</p>
            {known?.hint ? <p className={s.calloutHint}>{known.hint}</p> : null}
          </Callout>
        </div>
      ) : job.cancelRequested && isActiveJob(job.status) ? (
        <div style={{ marginTop: 14 }}>
          <Callout tone="warning" title="Cancelling">
            <p>The job stops after its current batch.</p>
          </Callout>
        </div>
      ) : null}
    </Card>
  );
}

interface Candidate {
  spec?: unknown;
  answer?: unknown;
  explanation?: unknown;
}

function ItemCard({ item, reveal, onEdit }: { item: StudioJobItem; reveal: boolean; onEdit: (id: string) => void }) {
  const canEdit = useCan("studio.edit");
  const p = item.puzzle;
  const cand = (item.candidate ?? null) as Candidate | null;
  // Candidates carry no id (the worker assigns one only to puzzles it keeps).
  const candSpec = p || !cand?.spec || typeof cand.spec !== "object" ? null : PuzzleSpecSchema.safeParse({ id: `candidate-${item.seq}`, ...(cand.spec as object) });
  const spec: PuzzleSpec | null = p?.spec ?? (candSpec?.success ? candSpec.data : null);
  const candAnswer = p ? null : AnswerSchema.safeParse(cand?.answer);
  const answer: AnswerValue | null = p ? p.answer : candAnswer?.success ? candAnswer.data : null;
  const explanation = p ? p.explanation : typeof cand?.explanation === "string" ? cand.explanation : null;
  const badge = itemBadge(item.status, item.verification, p?.reviewState);
  const inBank = p?.mode === "bank";

  return (
    <article className={s.itemCard} data-testid="job-item" data-status={item.status} data-badge={badge.state} data-puzzle={p?.id ?? ""}>
      <header className={s.itemHead}>
        <span className={s.seq}>#{item.seq}</span>
        {p ? (
          <Link to={inBank ? `/studio/bank?id=${encodeURIComponent(p.id)}` : `/puzzles/${encodeURIComponent(p.id)}`}>{p.id}</Link>
        ) : (
          <span className={s.itemId}>candidate</span>
        )}
        <VerificationChip badge={badge} />
        {p ? <StatusChip status={p.status} /> : null}
        {p && p.mode !== "bank" ? <Chip tone="info">{`${p.mode} · ${p.date ? formatShortDate(p.date) : "—"}`}</Chip> : null}
      </header>
      <div className={s.itemStage}>
        {spec ? (
          <PuzzleView spec={spec} answer={answer} reveal={reveal} compact />
        ) : (
          <p className={pages.faint}>The engine couldn&rsquo;t build a spec for this candidate.</p>
        )}
      </div>
      <IssuesList verification={item.verification} max={4} hideManualNote />
      <div className={s.itemText}>
        <span className={pages.answerLine} style={{ marginTop: 0 }}>
          <span className="monoLabel">Answer</span>
          {reveal ? <AnswerText answer={answer} /> : <span className={pages.hidden}>hidden · press R</span>}
          {reveal && item.label ? (
            <span className={s.itemLabel} title="The worker's label for this candidate (used to avoid repeats)">
              {item.label}
            </span>
          ) : null}
        </span>
        {reveal && explanation ? <p data-testid="explanation">{explanation}</p> : null}
      </div>
      {p && canEdit && (p.status === "draft" || p.status === "approved") ? (
        <div className={s.itemActions}>
          <ReviewActions puzzle={{ id: p.id, status: p.status, verification: item.verification }} />
          {p.status === "draft" ? (
            <Button size="sm" variant="ghost" onClick={() => onEdit(p.id)}>
              Edit
            </Button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
